import { useMsal } from '@azure/msal-react';
import { cloneDeep, forEach, result } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Api from '../Api';
import { IMaterial, IMaterialGroup, IMaterialItem, ISearchHistory, ISearchRequest, ISearchResultOption } from '../interfaces';


/* Potential Optimization TODO: */
/* https://medium.com/@andre.rabold/scaling-react-state-management-part-1-8e2cdc8f66bd */
/* https://medium.com/@andre.rabold/scaling-react-state-management-part-2-5a9bcda83b55*/


interface ISearchContext {
    query: string,
    setQuery: (query: string) => void,
    materialOut?: boolean,
    setMaterialOut: (materialOut: boolean) => void,
    keywords: IMaterialItem[],
    setKeywords: (keywords: IMaterialItem[]) => void,
    suggestions: IMaterialItem[],
    setSuggestions: (suggestions: IMaterialItem[]) => void,
    resultOptions: ISearchResultOption[],
    setResultOptions: (resultOptions: ISearchResultOption[]) => void,
    clearFilters: () => void,
    history: ISearchHistory[],
    clearHistory: () => void,
    search: (clearQuery?: boolean, keywords?: IMaterialItem[], page?: number) => void,
    results: IMaterialGroup[] | null,
    apiResults: IMaterialGroup[] | null,
    clearResults: () => void,
    exportResults: () => void,
    auditResults: () => void,
    searching: boolean,
    extendingSearch: boolean,
    exportingSearch: boolean,
    reloadMaterial: (materialId: string) => void,

}

export const SearchContext = React.createContext<ISearchContext>({
    query: "",
    setQuery: () => { },
    setMaterialOut: () => { },
    keywords: [],
    setKeywords: () => { },
    suggestions: [],
    setSuggestions: () => { },
    resultOptions: [],
    setResultOptions: () => { },
    clearFilters: () => { },
    history: [],
    clearHistory: () => { },
    search: (clearQuery?: boolean) => { },
    results: null,
    apiResults: null,
    clearResults: () => { },
    exportResults: () => { },
    auditResults: () => { },
    searching: false,
    extendingSearch: false,
    exportingSearch: false,
    reloadMaterial: (materialId: string) => { },
});

export const SearchContextProvider: React.FC = ({
    ...props
}) => {

    const { accounts } = useMsal();
    const userId = accounts ? accounts[0].localAccountId : ""
    const navigate = useNavigate();

    const setResultsFiltered = (r: IMaterialGroup[], o?: ISearchResultOption[]) => {

        let newResults = cloneDeep(r);
        let options = o ?? resultOptions;

        //filter the results

        options.forEach(option => {
            if (option.filteredValues) {
                option.filteredValues.forEach(value => {
                    newResults = newResults.filter(result => {

                        //try to get the property for the group
                        let property = (result as any)[option.propertyName];
                        if (property !== undefined) {
                            //if the group property matches the value we can
                            //filter out the entire group

                            if (property && property.id)
                                property = property.value; //flatten IMaterialItem

                            return property !== value;
                        }


                        //filter the individual materials
                        let materials: IMaterial[] = [];
                        result.materials?.forEach(material => {
                            let property = (material as any)[option.propertyName];

                            if (property && property.id) {
                                property = property.value;               //flatten IMaterialItem
                                if (property !== value)
                                    materials.push(material);
                            }
                            else if (Array.isArray(property)) {
                                property = property.map(x => x.value);   //flatten IMaterialItem[]
                                if (!property.includes(value))
                                    materials.push(material);
                            } else if (property !== value)
                                materials.push(material)

                        });

                        //if there are no materials left in the group
                        //filter out the entire group
                        if (materials.length === 0) return false;
                        else {
                            //update the materials for the group
                            result.materials = materials;
                            return true;
                        }


                    })
                })
            }
        })

        //order the results
        let property = options.filter(x => x.orderBy)[0]?.propertyName;

        if (property) {
            newResults.sort((a, b) => {

                let aProp = getProperty(a, property);
                let bProp = getProperty(b, property);


                //date comparison
                if (Number.isInteger(Date.parse(aProp))) {
                    return new Date(aProp).getTime() - new Date(bProp).getTime();
                }

                //number comparison
                if (Number.isInteger(aProp)) {
                    return aProp - bProp;
                }

                //ensure empty values are pushed to the end
                if (!aProp && !bProp) return 0;
                if (!aProp) return 1;
                if (!bProp) return -1;

                //string comparison
                return aProp.localeCompare(bProp);

            });
        }




        setResults(newResults);
    }

    const getProperty = (group: IMaterialGroup, property: string) => {


        //check if this is a group-level property
        let groupProp = (group as any)[property]
        if (groupProp != null)
            return groupProp;


        let materials = cloneDeep(group.materials ?? []);


        //quantity will be the sum of all
        //material matching reflecting 'Available'
        if (property === "quantity")
            return getSumProperty(materials, property);

        //clear empty properties out before comparing
        materials = materials.filter((x: any) => x[property]);
        if (materials.length > 0) {


            //what is the property type?
            let tProperty = (materials[0] as any)[property];
            let isDate = Number.isInteger(Date.parse(tProperty));
            let isMaterialItemArray = Array.isArray(tProperty);
            let isMaterialItem = tProperty ? tProperty.value !== undefined : false;

            if (isDate) {
                materials.sort((a: any, b: any) => {
                    return new Date(b.arrivalDate).getTime() - new Date(a.arrivalDate).getTime();
                })

            } else if (isMaterialItemArray) {
                materials.sort((a: any, b: any) => {
                    let aProp = a[property].sort()[0].value;
                    let bProp = b[property].sort()[0].value;

                    if (Number.isInteger(aProp)) {
                        return aProp - bProp;
                    } else return (aProp as string).localeCompare(bProp);

                });
            }
            else if (isMaterialItem) {
                materials.sort((a: any, b: any) => {

                    if (Number.isInteger(a[property].value))
                        return a[property].value - b[property].value;

                    return a[property].value.localeCompare(b[property].value);
                });
            }
            else {
                materials.sort((a: any, b: any) => {

                    if (Number.isInteger(a[property]))
                        return a[property] - b[property];

                    return a[property].localeCompare(b[property])
                })
            }

            let first = materials[0] as any;

            if (first) {
                if (isMaterialItemArray) return first[property].sort()[0].value;
                if (isMaterialItem) return first[property].value;
                return (first[property] ?? "");
            }

        }

        return "";
    }

    const getSumProperty = (materials: IMaterial[], property: string) => {
        //sum the property values

        let sum = materials.reduce((total, a: any) => total + a[property], 0);
        return sum;

    }

    const setKeywordsFiltered = (k: IMaterialItem[], searchOnUpdate?: boolean) => {
        setKeywords(k.filter(x => x !== undefined))
        if (searchOnUpdate)
            onSearch();
    }

    const getLocalHistory = () => {
        let r = window.localStorage.getItem("recentsearch_" + userId);
        if (r && r.length > 0) {
            let history: ISearchHistory[] = JSON.parse(r) as ISearchHistory[];


            //check for searches older than ~3months
            history = history.filter(item => {
                let itemDate: Date = new Date(item.date);
                let now: Date = new Date();
                let days = Math.ceil((now.getTime() - itemDate.getTime()) / (1000 * 3600 * 24));

                if (days <= 90)
                    return item;

                return null;
            });

            //clear out the old entries from local storage
            setLocalHistory(history);


            return history;
        } else return [];

    }
    const setLocalHistory = (h: ISearchHistory[]) => {
        window.localStorage.setItem("recentsearch_" + userId, JSON.stringify(h));
    }
    const clearLocalHistory = () => {
        window.localStorage.removeItem("recentsearch_" + userId);
    }

    const onSearch = (clearQuery?: boolean, overrideKeywords?: IMaterialItem[], page?: number) => {



        let k: IMaterialItem[] = [];

        if (overrideKeywords) {
            setKeywords(overrideKeywords);
            k = overrideKeywords;
        } else k = keywords;

        if (clearQuery)
            setQuery("");

        let searchRequest: ISearchRequest = {
            materialOut,
            query: k.filter(x => { return (x.id == null && x.propertyName == null) }).map(x => x.value).join(" "),
            keywords: k,
            pageIndex: page ?? 1
        }

        if (searchRequest.pageIndex === 1) {
            setResults(null);
            setSearching(true);
        } else setExtendingSearch(true);

        onHistory(searchRequest);
        Api.Search.materials(searchRequest).then((r: any) => {
            let results = r.results as IMaterialGroup[];

            if (results) {

                let fullResultSet: IMaterialGroup[] = results;

                setApiResults(prev => {
                    if (searchRequest.pageIndex !== 1 && prev !== null) {

                        const existingIds = new Set(prev.map(item => item.id));
                        const filteredResults = results.filter(item => !existingIds.has(item.id));
                        fullResultSet = [...prev, ...filteredResults];
                    }
                    return fullResultSet;
                });

                setResultsFiltered(fullResultSet);
                setSearching(false);
                setExtendingSearch(false);
            }
        });

    }

    const onResultOptions = (o: ISearchResultOption[]) => {


        setResultOptions(o);

        //if a search hasn't been performed, dont update results
        if (apiResults === null || apiResults.length === 0)
            return;

        setSearching(true);

        if (apiResults) {
            //we have already performed a search so just
            //update the results
            setResultsFiltered(apiResults, o);
        }
        else {
            onSearch(false);
        }

        setSearching(false);
    }
    const onClearFilters = () => {
        let newResultOptions = cloneDeep(resultOptions);
        newResultOptions.forEach(x => {
            x.filteredValues = []
        });
        onResultOptions(newResultOptions);
    }

    const onHistory = (request: ISearchRequest) => {

        let q = request.query;

        //dont add empty searches
        if (q.trim().length === 0) return;

        let current = cloneDeep(history);

        //remove previous matching searches
        current = current.filter(x => x.query !== q)

        current.unshift({
            query: q,
            keywords: request.keywords,
            date: new Date().toDateString()
        });

        setHistory(current);
        setLocalHistory(current);

    }
    const onClearHistory = () => { clearLocalHistory(); setHistory([]); }

    const onExportResults = () => {


        let searchRequest: ISearchRequest = {
            materialOut,
            query: keywords.filter(x => { return (x.id == null && x.propertyName == null) }).map(x => x.value).join(" "),
            keywords: keywords,
            pageIndex: 0
        }


        if (results) {
            setExportingSearch(true);
            Api.Search.export(searchRequest)
                .then((response: any) => response.blob())
                .then((blob) => {

                    const filename = `${keywords.filter(x => { return (x.id == null && x.propertyName == null) })
                        .map(x => x.value).join(" ")} - ${new Date().toUTCString()}.csv`;

                    const url = window.URL.createObjectURL(
                        new Blob([blob]),
                    );

                    const link: HTMLAnchorElement = document.createElement('a');

                    link.href = url;
                    link.setAttribute(
                        'download',
                        filename,
                    );
                    document.body.appendChild(link);
                    link.click();
                    link.parentNode?.removeChild(link);

                    setExportingSearch(false);
                }).catch((r) => {
                    console.log("error while exporting", r);
                    setExportingSearch(false);
                });
        }

    }

    const onAuditResults = () => {

        let searchRequest: ISearchRequest = {
            materialOut,
            query: keywords.filter(x => { return (x.id == null && x.propertyName == null) }).map(x => x.value).join(" "),
            keywords: keywords,
            pageIndex: 0
        }

        if (results) {
            setExportingSearch(true);
            Api.Audits.create(searchRequest, resultOptions).then((response: any) => {
                setExportingSearch(false);
                navigate('audit/update/' + response.materialAuditId);
            })
        }

        else alert("No materials to create an audit. Please complete a search for the materials required in the audit");
    }

    const onReloadMaterial = async (materialId: string) => {
        var results = cloneDeep(apiResults);

        results?.forEach(async r => {
            if (r.materials && r.materials.map(m => m.materialId).includes(materialId)) {
                await Api.Materials.get(materialId).then((response: any) => {
                    var updatedMaterial = response as IMaterial;
                    r.materials = r.materials?.filter(m => m.materialId != materialId);
                    r.materials?.push(updatedMaterial);

                    if (results) {
                        setApiResults(results);
                        setResultsFiltered(results);
                    }
                });
            }
        })

    }


    const [query, setQuery] = useState<string>("");
    const [searching, setSearching] = useState<boolean>(false);
    const [extendingSearch, setExtendingSearch] = useState<boolean>(false);
    const [exportingSearch, setExportingSearch] = useState<boolean>(false);
    const [results, setResults] = useState<IMaterialGroup[] | null>(null);
    const [apiResults, setApiResults] = useState<IMaterialGroup[] | null>(null);
    const [materialOut, setMaterialOut] = useState<boolean>();
    const [keywords, setKeywords] = useState<IMaterialItem[]>([]);
    const [suggestions, setSuggestions] = useState<IMaterialItem[]>([]);
    const [resultOptions, setResultOptions] = useState<ISearchResultOption[]>([]);
    const [history, setHistory] = useState<ISearchHistory[]>(getLocalHistory());

    useEffect(() => {
        if (materialOut !== undefined)
            onSearch();
    }, [materialOut])

    return <SearchContext.Provider value={{
        query,
        setQuery,
        materialOut,
        setMaterialOut,
        keywords,
        setKeywords: setKeywordsFiltered,
        suggestions,
        setSuggestions,
        resultOptions,
        setResultOptions: onResultOptions,
        clearFilters: onClearFilters,
        history,
        clearHistory: onClearHistory,
        search: onSearch,
        searching,
        extendingSearch,
        exportingSearch,
        results,
        apiResults,
        clearResults: () => setResults(null),
        reloadMaterial: onReloadMaterial,
        exportResults: onExportResults,
        auditResults: onAuditResults
    }} >
        {props.children}
    </SearchContext.Provider >

}